import { loc } from './loc.js';
import Enums from './Enums.js';

/*!
 * Copyright (c) 2015-2016, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */
class CustomError extends Error {
  constructor(message) {
    super(message);

    // 'Error' breaks prototype chain. Need to restore it for ES5.
    // https://github.com/microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    // Note: no need to adjust prototype in subclasses of 'CustomError'.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
class ConfigError extends CustomError {
  constructor(message) {
    super(message || loc('error.config'));
    this.name = Enums.CONFIG_ERROR;
  }
}
class UnsupportedBrowserError extends CustomError {
  constructor(message) {
    super(message || loc('error.unsupported.browser'));
    this.name = Enums.UNSUPPORTED_BROWSER_ERROR;
  }
}
class OAuthError extends CustomError {
  constructor(...args) {
    super(...args);
    this.name = Enums.OAUTH_ERROR;
  }
}
class RegistrationError extends CustomError {
  constructor(...args) {
    super(...args);
    this.name = Enums.REGISTRATION_FAILED;
  }
}

// Thrown when initiation of poll was cancelled.
class AuthStopPollInitiationError extends CustomError {
  constructor(...args) {
    super(...args);
    this.name = Enums.AUTH_STOP_POLL_INITIATION_ERROR;
  }
}
class U2FError extends CustomError {
  constructor(err) {
    var _err$xhr, _err$xhr$responseJSON;
    super((_err$xhr = err.xhr) === null || _err$xhr === void 0 ? void 0 : (_err$xhr$responseJSON = _err$xhr.responseJSON) === null || _err$xhr$responseJSON === void 0 ? void 0 : _err$xhr$responseJSON.errorSummary);
    this.xhr = void 0;
    this.name = Enums.U2F_ERROR;
    this.xhr = err.xhr;
  }
}
class WebAuthnError extends CustomError {
  constructor(err) {
    var _err$xhr2, _err$xhr2$responseJSO;
    super((_err$xhr2 = err.xhr) === null || _err$xhr2 === void 0 ? void 0 : (_err$xhr2$responseJSO = _err$xhr2.responseJSON) === null || _err$xhr2$responseJSO === void 0 ? void 0 : _err$xhr2$responseJSO.errorSummary);
    this.xhr = void 0;
    this.name = Enums.WEB_AUTHN_ERROR;
    this.xhr = err.xhr;
  }
}

// This is triggered only when code aborts webauthn browser prompt.
class WebauthnAbortError extends CustomError {
  constructor(...args) {
    super(...args);
    this.name = Enums.WEBAUTHN_ABORT_ERROR;
  }
}
class ConfiguredFlowError extends CustomError {
  constructor(message, flowSetting) {
    super(message);
    this.flowSetting = void 0;
    this.name = Enums.CONFIGURED_FLOW_ERROR;
    this.flowSetting = flowSetting;
  }
}

// Triggered when a user denies Local Network Access permission in Chrome, used to monitor LNA denials.
class ChromeLNADeniedError extends CustomError {
  constructor(...args) {
    super(...args);
    this.name = Enums.CHROME_LOCAL_NETWORK_ACCESS_DENIED_ERROR;
  }
}

export { AuthStopPollInitiationError, ChromeLNADeniedError, ConfigError, ConfiguredFlowError, OAuthError, RegistrationError, U2FError, UnsupportedBrowserError, WebAuthnError, WebauthnAbortError };
//# sourceMappingURL=Errors.js.map
